import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function isAccessActive(activeUntil) {
  if (!activeUntil) return false;
  const d = new Date(activeUntil);
  return !Number.isNaN(d.getTime()) && new Date() < d;
}

export default function PayPalSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Finalizing your PayPal support...");

  const orderId = useMemo(() => searchParams.get("token"), [searchParams]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!orderId) {
        setStatus("error");
        setMessage("Missing PayPal order id. Please try again.");
        return;
      }

      try {
        const delays = [0, 800, 1500];
        let captured = false;

        for (let i = 0; i < delays.length; i++) {
          if (delays[i]) await sleep(delays[i]);

          const res = await axios.post(`${API}/paypal/capture-order/${orderId}`);
          const resultStatus = res?.data?.status;

          if (resultStatus === "COMPLETED" || resultStatus === "already_processed") {
            captured = true;
            break;
          }
        }

        if (!captured) {
          setStatus("error");
          setMessage("Payment was not completed. Please try again.");
          return;
        }

        let unlocked = false;
        for (let i = 0; i < 4; i++) {
          if (i) await sleep(700);
          const me = await axios.get(`${API}/paypal/me`);
          const activeUntil = me?.data?.access?.activeUntil;
          if (isAccessActive(activeUntil)) {
            unlocked = true;
            break;
          }
        }

        if (!unlocked) {
          setStatus("error");
          setMessage("Payment captured, but access is still syncing. Please retry in a few seconds.");
          toast.error("Captured — access still syncing.");
          return;
        }

        if (cancelled) return;

        setStatus("success");
        setMessage("Support confirmed! Your access is unlocked and your Early Bird badge is active.");
        toast.success("Payment confirmed 🎉 Access unlocked.");

        setTimeout(() => {
          window.location.href = "/crm";
        }, 800);
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setMessage(err?.response?.data?.detail || "Could not confirm PayPal payment.");
        toast.error("Unable to confirm payment. Please retry.");
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [orderId]);

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#F9F9F7] p-6">
      <div className="noise-overlay" />
      <Card className="w-full max-w-lg border-gray-100 shadow-lg">
        <CardHeader className="space-y-2 text-center">
          <CardTitle className="text-2xl font-bold tracking-tight text-gray-900">
            {status === "success" ? "Payment confirmed" : status === "error" ? "Payment issue" : "Processing payment"}
          </CardTitle>
          <CardDescription className="text-base">{message}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-3">
          {status === "loading" && (
            <Button disabled className="w-full h-11 rounded-full bg-[#1A4D2E] text-white">
              Confirming...
            </Button>
          )}

          {status === "error" && (
            <>
              <Link to="/subscription-required" className="block">
                <Button className="w-full h-11 rounded-full bg-[#1A4D2E] hover:bg-[#14532D] text-white">
                  Retry PayPal Support
                </Button>
              </Link>
              <Link to="/crm" className="block">
                <Button variant="outline" className="w-full h-11 rounded-full">
                  Go to Dashboard
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
